import React, { useState, useEffect } from 'react';
import { businessHoursAPI } from '../../services/api';
import { formatDmy } from '../../utils/formatters';
import { FiClock, FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import toast from 'react-hot-toast';

const DAYS = [
  { id: 1, name: 'Dushanba' },
  { id: 2, name: 'Seshanba' },
  { id: 3, name: 'Chorshanba' },
  { id: 4, name: 'Payshanba' },
  { id: 5, name: 'Juma' },
  { id: 6, name: 'Shanba' },
  { id: 0, name: 'Yakshanba' },
];

const REASONS = {
  disabled: 'nazorat o\'chirilgan, 24/7',
  holiday: 'bugun bayram/dam kuni',
  'non-working-day': 'bugun ish kuni emas',
  'off-hours': 'ish vaqtidan tashqari',
  open: 'ish vaqti',
};

export default function BusinessHoursManagement() {
  const [hours, setHours] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [settings, setSettings] = useState({ enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [dirty, setDirty] = useState({});
  const [live, setLive] = useState(null);
  const [newHoliday, setNewHoliday] = useState({ holiday_date: '', name: '' });

  // Dialplan yozilmagan bo'lsa admin buni BILISHI kerak — aks holda "saqlandi" ko'rinadi-yu
  // Asterisk eski jadval bilan ishlaydi.
  const warnDialplan = (res) => {
    if (res?.data?.dialplan_warning) toast.error(res.data.dialplan_warning, { duration: 8000 });
  };

  const loadLive = async () => {
    try {
      const res = await businessHoursAPI.isOpen();
      setLive(res.data);
    } catch (err) {
      setLive(null);
    }
  };

  const load = async () => {
    try {
      const res = await businessHoursAPI.get();
      setHours(res.data.hours || []);
      setHolidays(res.data.holidays || []);
      setSettings(res.data.settings || { enabled: true });
      setDirty({});
    } catch (err) {
      toast.error('Ma\'lumotlarni olishda xato');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadLive(); }, []);

  const updateDay = (day, updates) => {
    setHours(prev => prev.map(h => h.day_of_week === day ? { ...h, ...updates } : h));
    setDirty(prev => ({ ...prev, [day]: true }));
  };

  const saveDay = async (day) => {
    const h = hours.find(x => x.day_of_week === day);
    if (!h) return;
    setSaving(prev => ({ ...prev, [day]: true }));
    try {
      const res = await businessHoursAPI.updateDay(day, {
        is_working_day: h.is_working_day,
        start_time: h.start_time,
        end_time: h.end_time,
      });
      warnDialplan(res);
      setDirty(prev => ({ ...prev, [day]: false }));
      toast.success('Saqlandi');
      loadLive();
    } catch (err) {
      toast.error('Saqlashda xato');
    } finally {
      setSaving(prev => ({ ...prev, [day]: false }));
    }
  };

  const toggleEnabled = async () => {
    const newVal = !settings.enabled;
    try {
      const res = await businessHoursAPI.updateSettings({ enabled: newVal });
      warnDialplan(res);
      setSettings({ ...settings, enabled: newVal });
      toast.success(newVal ? 'Ish vaqti nazorati yoqildi' : 'Ish vaqti nazorati o\'chirildi');
      loadLive();
    } catch (err) {
      toast.error('Xato');
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.holiday_date || !newHoliday.name) {
      toast.error('Sana va nom kiriting');
      return;
    }
    try {
      const res = await businessHoursAPI.addHoliday({
        holiday_date: newHoliday.holiday_date,
        name: newHoliday.name,
        is_working_day: false,
      });
      warnDialplan(res);
      setNewHoliday({ holiday_date: '', name: '' });
      toast.success('Qo\'shildi');
      load();
      loadLive();
    } catch (err) {
      toast.error('Xato');
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('O\'chirilsinmi?')) return;
    try {
      const res = await businessHoursAPI.deleteHoliday(id);
      warnDialplan(res);
      toast.success('O\'chirildi');
      load();
      loadLive();
    } catch (err) {
      toast.error('Xato');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>Ish vaqti</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
        Qo'ng'iroqlarni qabul qilish vaqtini boshqarish. Ish vaqtidan tashqarida qo'ng'iroq qiluvchilar "ish vaqti tugadi" xabarini eshitadi.
      </p>

      {/* Nazorat o'chirilgan bo'lsa — pastdagi jadval hech qanday ta'sir qilmaydi. Buni yashirmaymiz. */}
      {!settings.enabled && (
        <div style={styles.dangerBanner}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ⚠️ Ish vaqti nazorati O'CHIRILGAN
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            Qo'ng'iroqlar hozir <strong>24/7 qabul qilinmoqda</strong>. Pastdagi hafta jadvali va
            "Ish vaqtidan tashqari" ovozli xabar <strong>ishlamaydi</strong> — ular faqat nazorat
            yoqilganda kuchga kiradi.
          </div>
          <button onClick={toggleEnabled} style={styles.dangerBtn}>
            Ish vaqti nazoratini YOQISH
          </button>
        </div>
      )}

      {/* Enable toggle */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Ish vaqti nazorati</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              O'chirilsa, barcha qo'ng'iroqlar 24/7 qabul qilinadi
            </div>
            {live && (
              <div style={{ fontSize: 13, marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: live.is_open ? '#10b981' : '#ef4444', display: 'inline-block',
                }} />
                <span style={{ color: 'var(--text-secondary)' }}>
                  Hozir: <strong style={{ color: live.is_open ? '#10b981' : '#ef4444' }}>
                    {live.is_open ? 'QABUL QILINADI' : 'QABUL QILINMAYDI'}
                  </strong>
                  {' — '}{REASONS[live.reason] || live.reason}
                  {live.start_time && ` (${live.start_time.slice(0, 5)}–${live.end_time.slice(0, 5)})`}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={toggleEnabled}
            style={{
              padding: '8px 20px',
              background: settings.enabled ? '#10b981' : '#6b7280',
              border: 'none', borderRadius: 8, color: 'white',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            {settings.enabled ? 'Yoqilgan' : 'O\'chirilgan'}
          </button>
        </div>
      </div>

      {/* Days */}
      <div style={styles.section}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Hafta jadvali</div>
        {DAYS.map(d => {
          const h = hours.find(x => x.day_of_week === d.id);
          if (!h) return null;
          return (
            <div key={d.id} style={styles.dayRow}>
              <div style={{ flex: '0 0 120px', fontWeight: 600 }}>{d.name}</div>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={h.is_working_day}
                  onChange={(e) => updateDay(d.id, { is_working_day: e.target.checked })}
                />
                Ish kuni
              </label>
              <input
                type="time"
                value={h.start_time?.slice(0, 5) || '09:00'}
                onChange={(e) => updateDay(d.id, { start_time: e.target.value })}
                disabled={!h.is_working_day}
                style={styles.timeInput}
              />
              <span style={{ color: 'var(--text-muted)' }}>—</span>
              <input
                type="time"
                value={h.end_time?.slice(0, 5) || '18:00'}
                onChange={(e) => updateDay(d.id, { end_time: e.target.value })}
                disabled={!h.is_working_day}
                style={styles.timeInput}
              />
              {dirty[d.id] && (
                <span style={styles.dirtyTag}>saqlanmagan</span>
              )}
              <button
                onClick={() => saveDay(d.id)}
                disabled={saving[d.id]}
                style={{
                  ...styles.saveBtn,
                  ...(dirty[d.id] ? { marginLeft: 8 } : {}),
                  ...(saving[d.id] ? { opacity: 0.6, cursor: 'default' } : {}),
                }}
              >
                <FiSave size={14} /> {saving[d.id] ? 'Saqlanmoqda…' : 'Saqlash'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Holidays */}
      <div style={styles.section}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Bayramlar va dam kunlari</div>
        
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="date"
            value={newHoliday.holiday_date}
            onChange={(e) => setNewHoliday({ ...newHoliday, holiday_date: e.target.value })}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Bayram nomi"
            value={newHoliday.name}
            onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
            style={{ ...styles.input, flex: 1 }}
          />
          <button onClick={addHoliday} style={styles.addBtn}>
            <FiPlus size={14} /> Qo'shish
          </button>
        </div>

        {holidays.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Bayramlar qo'shilmagan
          </div>
        ) : (
          <div>
            {holidays.map(h => (
              <div key={h.id} style={styles.holidayRow}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{h.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {formatDmy(h.holiday_date)}
                  </div>
                </div>
                <button
                  onClick={() => deleteHoliday(h.id)}
                  style={{ ...styles.saveBtn, background: '#ef4444' }}
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...styles.section, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
        <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
          <strong>Eslatma:</strong> Ish vaqtidan tashqarida qo'ng'iroq qiluvchilar avval "Xush kelibsiz" xabarini, keyin "Ish vaqti tugagani" xabarini eshitadi. Keyin qo'ng'iroq avtomatik yakunlanadi.
          <br /><br />
          <strong>Audio yuklash:</strong> "Musiqa va tovushlar" tabida "Ish vaqtidan tashqari" fayl yuklash mumkin.
        </div>
      </div>
    </div>
  );
}

const styles = {
  section: {
    background: '#1e293b', border: '1px solid #2d3a4f',
    borderRadius: 12, padding: 20, marginBottom: 20,
  },
  dayRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 0', borderBottom: '1px solid #2d3a4f',
  },
  checkboxLabel: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 13, color: 'var(--text-secondary)',
    cursor: 'pointer', minWidth: 100,
  },
  timeInput: {
    padding: '8px 12px',
    background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 6, color: '#f1f5f9', fontSize: 13,
    fontFamily: 'inherit',
  },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#3b82f6', border: 'none',
    borderRadius: 6, color: 'white', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', marginLeft: 'auto',
  },
  input: {
    padding: '10px 12px',
    background: '#0f172a', border: '1px solid #2d3a4f',
    borderRadius: 8, color: '#f1f5f9', fontSize: 14,
    fontFamily: 'inherit',
  },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 16px', background: '#10b981', border: 'none',
    borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  holidayRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: 12, background: '#0f172a', borderRadius: 8, marginBottom: 8,
  },
  dangerBanner: {
    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.45)',
    borderRadius: 12, padding: 20, marginBottom: 20, color: '#fecaca',
  },
  dangerBtn: {
    marginTop: 14, padding: '10px 18px', background: '#ef4444', border: 'none',
    borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  dirtyTag: {
    marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#fbbf24',
    background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)',
    borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap',
  },
};
